import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, MapPin, Quote } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import ContactForm from "@/components/site/ContactForm";
import { TemplateShell } from "./shared/TemplateShell";
import { OrganicHeader, OrganicFooter, ORGANIC_MAIN_CLASS } from "./chrome/OrganicChrome";
import { useSiteContent, useTestimonials, useBlogPosts, useServices } from "./shared/useSiteData";
import BlogGrid from "./shared/BlogGrid";
// Relative, not "@/": no craco jest moduleNameMapper for "@/" — see MinimalTemplate.jsx's
// identical note on its own "../lib/siteContent" import.
import { formatQuote } from "../lib/testimonials";

/**
 * Warm / Organic — terreux, fluide, radii irréguliers, mosaïque d'images,
 * fonds chauds, bento décalé, ombres douces, accent manuscrit (Caveat).
 */
/**
 * The shared ContactForm in this theme's idiom. It lives INSIDE the coloured arch panel, so
 * the fields are translucent-on-primary with the same irregular radii as the bento cards
 * (one corner cut on each), and the labels become placeholders to keep the arch uncluttered.
 */
const FORM_CLASSES = {
  grid: "grid grid-cols-1 sm:grid-cols-2 gap-4 text-left",
  fieldWide: "sm:col-span-2",
  input: "w-full rounded-[1.75rem] rounded-tr-lg bg-white/15 border border-white/25 px-5 py-3.5 text-white placeholder:text-white/60 outline-none focus:bg-white/25 transition",
  textarea: "w-full rounded-[2rem] rounded-bl-lg bg-white/15 border border-white/25 px-5 py-3.5 text-white placeholder:text-white/60 outline-none focus:bg-white/25 transition resize-none",
  hint: "mt-3 text-xs text-white/70",
  actions: "mt-6 flex items-center justify-center gap-4 flex-wrap",
  submit: "inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-white bg-[var(--tpl-accent)] hover:opacity-90 transition-opacity disabled:opacity-60",
  success: "text-white font-semibold",
  error: "text-white font-semibold underline",
};

export default function OrganicTemplate({ template, toggles = {} }) {
  const c = useSiteContent(toggles);
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  const services = useServices();
  const [ti, setTi] = React.useState(0);
  const show = (k) => toggles?.[k] !== false;

  const script = { fontFamily: "'Caveat', cursive" };

  return (
    <TemplateShell template={template} branding={c.branding}>
      <main data-testid="tpl-organic" className={ORGANIC_MAIN_CLASS}>
        {/* The header is the theme's CHROME (chrome/OrganicChrome.jsx), the same component
            the interior pages render through ThemedPageLayout — never an inline copy. */}
        <OrganicHeader toggles={toggles} />

        {/* HERO — asymétrique + mosaïque */}
        <section id="top" className="pt-36 md:pt-44 pb-24 px-6 md:px-10" data-testid="hero-section">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
            {/* BUG-233: a grid item's default `min-width: auto` floors its width at its
                min-content size — for this column that's the longest unbreakable word in
                the heading (French/Spanish hero copy runs longer than the English sample),
                so at narrow widths the column was forced wider than the viewport instead of
                shrinking to the available track. `min-w-0` lets the item shrink to the
                track's actual size; `break-words` on the heading (inherited by its `block`
                spans) then lets that now-narrower box wrap a too-long word instead of
                letting it spill past the edge, where `ORGANIC_MAIN_CLASS`'s
                `overflow-hidden` would silently clip it. `min-w-0` changes nothing once
                the column already fits its content (English at every width, every locale
                at lg: and up — measured: the column's own width there is 564px either
                way). `break-words` on its own DOES change the desktop reading order — at
                lg: the same 564px column is exactly narrow enough that "accompagnement"
                does not fit even alone on its line, and `break-words` lets it fracture
                mid-word where the pre-fix rendering instead let the word spill into the
                gutter (never reaching the image column, so never reported as an
                overflowing element, but visually different) — so `lg:break-normal`
                deliberately turns `break-words` back off from the lg: breakpoint up,
                keeping the desktop two-column reading exactly as it measured before this
                fix (verified: identical hero height at every locale at 768/1280/1440px). */}
            <div className="min-w-0">
              {c.hero.script && <p style={script} className="text-3xl mb-2" data-testid="hero-eyebrow"><span style={{ color: "var(--tpl-accent)" }}>{c.hero.script}</span></p>}
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="tpl-head text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] break-words lg:break-normal">
                {c.hero.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
              </motion.h1>
              {c.hero.sub && <p className="mt-7 text-lg md:text-xl opacity-70 max-w-lg leading-relaxed">{c.hero.sub}</p>}
              <div className="mt-9 flex flex-wrap gap-4">
                <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-white hover:opacity-90 transition-opacity" style={{ background: "var(--tpl-primary)" }}>{c.hero.cta} <ArrowRight size={16} /></a>
                <a href="#approach" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold border" style={{ borderColor: "var(--tpl-primary)" }}>{c.hero.ctaSecondary}</a>
              </div>
              {/* BUG-196: an un-wrapped `flex` row could not drop a stat to a second
                  line, so three items wider than the phone dragged the row (and the
                  page, per BUG-197) sideways. `flex-wrap` is the fix. */}
              <div data-testid="hero-stats" className="mt-12 flex flex-wrap gap-8">
                {c.hero.stats.map((s, i) => (
                  <div key={i}><div className="tpl-head text-3xl font-semibold" style={{ color: "var(--tpl-primary)" }}>{s.v}</div><div className="text-xs uppercase tracking-widest opacity-50 mt-1">{s.l}</div></div>
                ))}
              </div>
            </div>
            <div className="relative grid grid-cols-2 gap-4">
              <motion.img initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} src={c.images.heroPortrait} alt="" data-testid="hero-portrait" className="col-span-1 row-span-2 w-full h-full object-cover rounded-[3rem] rounded-tr-lg" style={{ minHeight: 420 }} />
              <motion.img initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} src={c.images.hotStone} alt="" className="w-full h-44 object-cover rounded-[2.5rem] rounded-bl-lg" />
              <motion.img initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} src={c.images.mortar} alt="" className="w-full h-44 object-cover rounded-[2.5rem] rounded-tl-lg" />
            </div>
          </div>
        </section>

        {/* APPROACH / pillars */}
        {show("approach") && (
          <section id="approach" className="py-24 md:py-32 px-6 md:px-10" style={{ background: "var(--tpl-secondary)" }} data-testid="approach-section">
            <div className="max-w-7xl mx-auto">
              <span className="text-xs uppercase tracking-[0.3em] opacity-50">{c.approach.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-5xl font-semibold mt-4 max-w-2xl whitespace-pre-line">{c.approach.title}</h2>
              <p className="mt-5 opacity-70 text-lg leading-relaxed max-w-2xl">{c.approach.lead}</p>
              <div className="mt-12 grid sm:grid-cols-2 gap-6">
                {c.approach.pillars.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} data-testid={`approach-pillar-${i}`} className={`p-8 bg-[var(--tpl-bg)] shadow-[0_20px_50px_-30px_rgba(62,42,33,0.4)] ${i % 2 ? "rounded-[2.5rem] rounded-tr-lg" : "rounded-[2.5rem] rounded-bl-lg"}`}>
                    <h3 className="tpl-head text-2xl font-semibold">{p.title}</h3>
                    <p className="mt-3 opacity-70 leading-relaxed">{p.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* STORY */}
        {show("story") && (
          <section id="story" className="py-24 md:py-32 px-6 md:px-10" data-testid="story-section">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <div className="lg:sticky lg:top-28">
                  <span className="text-xs uppercase tracking-[0.3em] opacity-50">{c.story.eyebrow}</span>
                  <h2 className="tpl-head text-4xl md:text-5xl font-semibold mt-4 whitespace-pre-line">{c.story.title}</h2>
                  <p className="mt-5 opacity-70 text-lg leading-relaxed">{c.story.lead}</p>
                  <img src={c.images.apothecary} alt="" className="mt-8 w-full h-60 object-cover rounded-[2.5rem] rounded-br-lg" />
                </div>
              </div>
              <div className="lg:col-span-7 space-y-10">
                {c.story.chapters.map((ch, i) => (
                  <motion.div key={ch.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid={`story-chapter-${ch.n}`} className="flex gap-6">
                    <span style={script} className="text-5xl" >{ch.n}</span>
                    <div><h3 className="tpl-head text-2xl font-semibold">{ch.title}</h3><p className="mt-2 opacity-70 leading-relaxed">{ch.body}</p></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SERVICES — bento décalé */}
        {show("services") && services.length > 0 && (
          <section id="services" className="py-24 md:py-32 px-6 md:px-10" data-testid="services-section">
            <div className="max-w-7xl mx-auto">
              <span className="text-xs uppercase tracking-[0.3em] opacity-50">{c.services.eyebrow}</span>
              <h2 className="tpl-head text-4xl md:text-5xl font-semibold mt-4 max-w-2xl whitespace-pre-line">{c.services.title}</h2>
              <p className="mt-5 opacity-70 text-lg leading-relaxed max-w-2xl">{c.services.lead}</p>
              <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`service-card-${i}`}
                    className={`p-8 flex flex-col group transition-transform hover:-translate-y-1 ${i === 0 ? "lg:row-span-2 text-white rounded-[3rem] rounded-tl-lg" : "rounded-[2.5rem] rounded-br-lg"}`}
                    style={{ background: i === 0 ? "var(--tpl-primary)" : "var(--tpl-secondary)" }}>
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-widest opacity-70"><span style={i === 0 ? { color: "var(--tpl-accent)" } : {}}>{s.tag}</span><span>{s.duration}</span></div>
                    <h3 className="tpl-head text-2xl md:text-3xl font-semibold mt-5">{s.title}</h3>
                    <p className={`mt-3 leading-relaxed ${i === 0 ? "text-white/80" : "opacity-70"}`}>{s.body}</p>
                    <a href={tenantHref("/reserver")} className="mt-auto pt-6 inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={i === 0 ? { color: "var(--tpl-accent)" } : {}}>{c.services.cta} <ArrowUpRight size={15} /></a>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS */}
        {show("testimonials") && testimonials.length > 0 && (
          <section id="testimonials" className="py-28 px-6" style={{ background: "var(--tpl-secondary)" }} data-testid="testimonials-section">
            <div className="max-w-3xl mx-auto text-center">
              <Quote size={36} style={{ color: "var(--tpl-accent)" }} className="mx-auto" />
              <motion.p key={ti} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tpl-head text-2xl md:text-3xl font-medium mt-6 leading-snug" data-testid={`testimonial-${ti}`}>{formatQuote(testimonials[ti]?.quote)}</motion.p>
              <div style={script} className="text-3xl mt-5" >— {testimonials[ti]?.name}</div>
              <div className="mt-6 flex justify-center gap-2">
                {testimonials.map((_, i) => <button key={i} onClick={() => setTi(i)} className={`w-2.5 h-2.5 rounded-full ${i === ti ? "" : "opacity-30"}`} style={{ background: "var(--tpl-primary)" }} aria-label={`avis ${i + 1}`} />)}
              </div>
            </div>
          </section>
        )}

        {/* BLOG */}
        {show("blog") && posts.length > 0 && (
          <section id="blog" className="py-24 md:py-32 px-6 md:px-10" data-testid="blog-section">
            <BlogGrid
              posts={posts}
              gridClassName="max-w-7xl mx-auto gap-8"
              cardClassName="group"
              renderCard={(p) => (
                <>
                  <img src={p.cover} alt="" className="w-full h-56 object-cover rounded-[2.5rem] rounded-tr-lg mb-5 group-hover:scale-[1.02] transition-transform" />
                  <div className="text-[11px] uppercase tracking-widest opacity-50">{p.tag} · {p.date}</div>
                  <h3 className="tpl-head text-xl font-semibold mt-2">{p.title}</h3>
                  <p className="mt-2 opacity-70 text-sm leading-relaxed">{p.excerpt}</p>
                </>
              )}
            />
          </section>
        )}

        {/* CONTACT + FOOTER (arch) */}
        {show("contact") && (
          <section id="contact" className="pt-24 px-6 md:px-10" data-testid="contact-section">
            <div className="max-w-3xl mx-auto text-center rounded-t-[4rem] px-8 pt-16 pb-20" style={{ background: "var(--tpl-primary)", color: "#fff" }}>
              <h2 className="tpl-head text-4xl md:text-5xl font-semibold whitespace-pre-line">{c.contact.title}</h2>
              <p className="mt-5 text-white/80 max-w-md mx-auto">{c.contact.lead}</p>
              <ContactForm className="mt-10" classNames={FORM_CLASSES} showLabels={false} icon={<ArrowRight size={16} />} />
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5">
                <a href={`mailto:${c.contact.info.email}`} className="inline-flex items-center gap-2 text-white/90 hover:text-white" data-testid="contact-email-link"><Mail size={16} /> {c.contact.info.email}</a>
                <span className="inline-flex items-center gap-2 text-white/90"><MapPin size={16} /> {c.contact.info.place}</span>
              </div>
              <a href={tenantHref("/reserver")} className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold border border-white/40 hover:bg-white/10 transition-colors">{c.nav.book} <ArrowRight size={16} /></a>
            </div>
          </section>
        )}

        <OrganicFooter toggles={toggles} />
      </main>
    </TemplateShell>
  );
}
